import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useListTruckDriverOnShiftsQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import { useListRoutesQuery } from "~/shared/api/routes/route.grpc";
import { useGetLocationByIdQuery } from "~/shared/api/locations/locations.grpc";
import { MapObject } from "~/features/map-view/ui/map-view";
import type { FocusPoint } from "~/features/map-view/components/FlyToPoint";
import { buildStepPins, numberRouteJobs } from "~/features/map-view/model/helpers/step-pin-items";
import { InfoType } from "~/features/info-panel/model/useInfoPanel";
import {
  matchesCompactSearch,
  parseLocalDateParam,
  toDateParam,
  toTimestamp,
  fromTimestamp,
} from "@racoon/lib";
import { getUtcDayRange } from "../../utils/dayRange";
import { getRoutesJobsCount } from "../../utils/routeMetrics";
import type {
  SidebarItem,
  SidebarMode,
  SidebarSection,
} from "../../components/msw-route-list/msw-route-list.types";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { groupByTeam, useDriverTeams } from "~/shared/lib/driver-teams";
import { fullName } from "~/shared/helpers";
import { humanTruckType } from "~/pages/dashboard/autopark/helpers";
import { ConnectProto } from "@racoon/api";
import { isShiftRunning, isShiftTerminal } from "~/shared/lib/shift-status";
import { Namespace, useLang, useT } from "@racoon/i18n";
import { isTruckTypeCompatibleWithRouteCategory } from "./jobCategoryRouteCompatibility";
import { truckTypeOf } from "~/shared/lib/truck-type";

const EMPTY_ROUTES: ConnectProto.Route[] = [];

const isSameId = (a: any, b: any): boolean => {
  if (a == null || b == null) return false;
  try {
    return BigInt(a) === BigInt(b);
  } catch {
    return String(a) === String(b);
  }
};

type TransferContext = {
  selectedDate?: Date;
  routes: ConnectProto.Route[];
  selectionMap: Map<bigint, Set<bigint>>;
};

type Params = {
  onTransferContextChange?: (context: TransferContext) => void;
  routeCategory?: number;
  truckType?: ConnectProto.TruckType;
};

export const findPreferredShift = (shifts: ConnectProto.DriverOnShift[]) =>
  shifts.find((shift) => shift.status === ConnectProto.DriverOnShiftStatus.ACTIVE) ??
  shifts.find((shift) => isShiftRunning(shift.status)) ??
  shifts[0];

/** The sidebar item a shift belongs to: its truck, or its driver. */
const shiftOwnerId = (shift: ConnectProto.DriverOnShift, mode: SidebarMode) =>
  mode === "drivers" ? shift.driverId : shift.truckId;

const sortShiftsByCreatedAt = (
  left: ConnectProto.DriverOnShift,
  right: ConnectProto.DriverOnShift,
) => {
  const leftCreatedAt = fromTimestamp(left.createdAt)?.getTime() ?? 0;
  const rightCreatedAt = fromTimestamp(right.createdAt)?.getTime() ?? 0;

  if (leftCreatedAt !== rightCreatedAt) {
    return leftCreatedAt - rightCreatedAt;
  }

  if (left.id < right.id) return -1;
  if (left.id > right.id) return 1;
  return 0;
};

export const useMswRouteListState = ({
  onTransferContextChange,
  routeCategory,
  truckType,
}: Params) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Frozen snapshot of URL params at mount. We read URL once here to seed
  // initial state; all subsequent URL writes happen imperatively inside
  // handlers/helpers. This avoids state↔URL sync effects, which historically
  // raced with async data loading and wiped URL params before init ran.
  const initialUrlParamsRef = React.useRef({
    date: searchParams.get("date"),
    mswShiftId: searchParams.get("mswShiftId"),
    mswRouteId: searchParams.get("mswRouteId"),
    sidebar: searchParams.get("sidebar"),
  });

  // Batched into one navigation: each setSearchParams starts a navigation that cancels any
  // still pending, which would drop the earlier update's params.
  const pendingUrlUpdates = React.useRef<Record<string, string | null> | null>(null);
  const searchParamsRef = React.useRef(searchParams);
  searchParamsRef.current = searchParams;
  const updateUrlParams = React.useCallback(
    (updates: Record<string, string | null>) => {
      if (pendingUrlUpdates.current) {
        Object.assign(pendingUrlUpdates.current, updates);
        return;
      }
      pendingUrlUpdates.current = { ...updates };
      queueMicrotask(() => {
        const batch = pendingUrlUpdates.current ?? {};
        pendingUrlUpdates.current = null;
        // Every setSearchParams call navigates, even to the same URL.
        const current = searchParamsRef.current;
        if (Object.entries(batch).every(([key, value]) => current.get(key) === value)) return;
        // Recorded now: the router re-renders with these params only once it navigates.
        const applied = new URLSearchParams(current);
        for (const [key, value] of Object.entries(batch)) {
          if (value === null) applied.delete(key);
          else applied.set(key, value);
        }
        searchParamsRef.current = applied;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            for (const [key, value] of Object.entries(batch)) {
              if (value === null) next.delete(key);
              else next.set(key, value);
            }
            return next;
          },
          { replace: true },
        );
      });
    },
    [setSearchParams],
  );

  const [selectedDate, setSelectedDateState] = React.useState<Date | undefined>(
    () => parseLocalDateParam(initialUrlParamsRef.current.date ?? undefined) ?? new Date(),
  );
  const setSelectedDate = React.useCallback(
    (date: Date | undefined) => {
      setSelectedDateState(date);
      updateUrlParams({ date: date ? toDateParam(date) : null });
    },
    [updateUrlParams],
  );

  const [vehicleSearch, setVehicleSearch] = React.useState("");
  const [sidebarMode, setSidebarModeState] = React.useState<SidebarMode>(() =>
    initialUrlParamsRef.current.sidebar === "drivers" ? "drivers" : "trucks",
  );
  // The selected truck or driver, per `sidebarMode`.
  const [selectedId, setSelectedId] = React.useState<bigint | null>(null);
  const [selectedShiftId, setSelectedShiftId] = React.useState<bigint | null>(null);
  const [expandedRouteByShiftId, setExpandedRouteByShiftId] = React.useState<
    Map<bigint, bigint | null>
  >(new Map());
  const [routesByShiftId, setRoutesByShiftId] = React.useState<Map<bigint, ConnectProto.Route[]>>(
    new Map(),
  );
  const [, setLoadingByShiftId] = React.useState<Map<bigint, boolean>>(new Map());
  const [selectionByShiftId, setSelectionByShiftId] = React.useState<
    Map<bigint, Map<bigint, Set<bigint>>>
  >(new Map());

  const hasInitializedShiftSelectionRef = React.useRef(false);
  const hasAppliedInitialRouteFromUrlRef = React.useRef(false);
  const previousShiftIdRef = React.useRef<bigint | null>(null);
  const shiftsWithUserClosedRouteRef = React.useRef<Set<bigint>>(new Set());
  const dayRange = React.useMemo(() => getUtcDayRange(selectedDate), [selectedDate]);
  const { activeCompanyId } = useAuthSession();

  // Atomic state+URL writers. Use these instead of bare setSelectedShiftId /
  // setExpandedRouteByShiftId so the URL never drifts away from the rendered
  // selection.
  const applySelectedShift = React.useCallback(
    (shiftId: bigint | null) => {
      setSelectedShiftId(shiftId);
      updateUrlParams({ mswShiftId: shiftId ? String(shiftId) : null });
    },
    [updateUrlParams],
  );

  const applyExpandedRoute = React.useCallback(
    (shiftId: bigint, routeId: bigint | null) => {
      setExpandedRouteByShiftId((prev) => {
        const next = new Map(prev);
        next.set(shiftId, routeId);
        return next;
      });
      updateUrlParams({
        mswShiftId: String(shiftId),
        mswRouteId: routeId ? String(routeId) : null,
      });
    },
    [updateUrlParams],
  );

  const focusedExpandedRouteId = React.useMemo(() => {
    if (!selectedShiftId) return null;
    return expandedRouteByShiftId.get(selectedShiftId) ?? null;
  }, [expandedRouteByShiftId, selectedShiftId]);

  // `currentData`, not `data`: after the date or company changes, the previous
  // response must not keep its shifts and routes on screen (and editable) until
  // the new one arrives.
  const { currentData: truckDriverShiftsResponse, refetch: refetchShifts } =
    useListTruckDriverOnShiftsQuery(
      {
        ownerOrganizationId: BigInt(activeCompanyId ?? 0),
        datetimeFilter: {
          from: toTimestamp(new Date(dayRange.start)),
          to: toTimestamp(new Date(dayRange.end)),
        },
        truckTypes: truckType !== undefined ? [truckType] : [],
      },
      {
        skip: !selectedDate || !activeCompanyId,
      },
    );

  const shiftsData = React.useMemo(() => {
    const list = truckDriverShiftsResponse?.truckDriverOnShifts ?? [];
    const allShifts: ConnectProto.DriverOnShift[] = [];
    for (const group of list) {
      const truck = group.truck;
      const shifts = group.driverOnShifts ?? [];
      for (const shift of shifts) {
        allShifts.push({
          ...shift,
          id: BigInt(shift.id),
          truckId: BigInt(shift.truckId),
          driverId: shift.driverId != null ? BigInt(shift.driverId) : undefined,
          truck,
        } as any);
      }
    }
    return allShifts;
  }, [truckDriverShiftsResponse]);

  // The sidebar only shows trucks matching the category picked in FilterBlock; the
  // truck type is narrowed by the server (`truckTypes` above).
  const allVehicleShifts = React.useMemo(() => {
    return (shiftsData ?? [])
      .filter((shift) => shift.truckId && shift.truckId !== 0n)
      .filter((shift) =>
        isTruckTypeCompatibleWithRouteCategory(
          shift.truck?.type as ConnectProto.TruckType | undefined,
          routeCategory as ConnectProto.RouteCategory | undefined,
        ),
      )
      .sort(sortShiftsByCreatedAt);
  }, [shiftsData, routeCategory]);

  const allShiftIds = React.useMemo(() => allVehicleShifts.map((s) => s.id), [allVehicleShifts]);

  const {
    currentData: allRoutesResponse,
    isFetching: allRoutesFetching,
    refetch: refetchAllRoutes,
  } = useListRoutesQuery(
    {
      ownerOrganizationIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
      driverOnShiftIds: allShiftIds,
      ...(routeCategory !== undefined ? { routeCategories: [routeCategory] } : {}),
    },
    { skip: !activeCompanyId || allShiftIds.length === 0 },
  );
  // Loading = nothing matching the current request yet (a refetch of the same
  // request keeps its data and is not "loading").
  const allRoutesLoading = allRoutesFetching && !allRoutesResponse;

  const allRoutesByShiftId = React.useMemo(() => {
    const map = new Map<bigint, ConnectProto.Route[]>();
    for (const route of allRoutesResponse?.routes ?? []) {
      if (!route.driverOnShiftId) continue;
      const shiftId = BigInt(route.driverOnShiftId);
      const existing = map.get(shiftId) ?? [];
      existing.push(route);
      map.set(shiftId, existing);
    }
    return map;
  }, [allRoutesResponse]);

  // Shifts the sidebar can list in the current mode: a shift without a driver has no driver row.
  const sidebarShifts = React.useMemo(
    () => allVehicleShifts.filter((shift) => shiftOwnerId(shift, sidebarMode) != null),
    [allVehicleShifts, sidebarMode],
  );

  const shiftLabel = React.useCallback(
    (shift: ConnectProto.DriverOnShift) =>
      sidebarMode === "drivers"
        ? fullName(shift.driver) || String(shift.driverId)
        : (shift.truck?.plateNumber ?? "-"),
    [sidebarMode],
  );

  const displayedVehicleShifts = React.useMemo(
    () => sidebarShifts.filter((shift) => matchesCompactSearch(shiftLabel(shift), vehicleSearch)),
    [sidebarShifts, vehicleSearch, shiftLabel],
  );

  const teams = useDriverTeams(activeCompanyId, sidebarMode !== "drivers");

  const t = useT();
  const { lang } = useLang();

  // Trucks grouped by type; drivers grouped by team. A driver in several teams is
  // listed under each; drivers in none go to the last, "no team" section.
  const sidebarSections = React.useMemo<SidebarSection[]>(() => {
    const items = new Map<string, SidebarItem>();
    for (const shift of displayedVehicleShifts) {
      const id = shiftOwnerId(shift, sidebarMode)!;
      const item = items.get(String(id));
      if (item) item.shifts.push(shift);
      else
        items.set(String(id), { id, label: shiftLabel(shift), shifts: [shift], assignedJobs: 0 });
    }
    for (const item of items.values()) {
      const routes = item.shifts.flatMap((shift) => allRoutesByShiftId.get(shift.id) ?? []);
      item.assignedJobs = getRoutesJobsCount(routes, ConnectProto.JobStatus.ASSIGNED);
    }
    const byLabel = (a: { label?: string }, b: { label?: string }) =>
      (a.label ?? "").localeCompare(b.label ?? "", lang);

    if (sidebarMode === "trucks") {
      const sections = new Map<string, SidebarSection>();
      for (const item of items.values()) {
        const truck = item.shifts[0].truck;
        const type = truck ? truckTypeOf(truck) : undefined;
        const key = String(type ?? "undefined");
        const section = sections.get(key);
        if (section) section.items.push(item);
        else sections.set(key, { key, label: humanTruckType(type, t), items: [item] });
      }
      return Array.from(sections.values()).sort(byLabel);
    }

    return groupByTeam(
      Array.from(items.values()).sort(byLabel),
      teams,
      (item) => String(item.id),
      t(Namespace.Common, "noTeam"),
    );
  }, [displayedVehicleShifts, sidebarMode, shiftLabel, allRoutesByShiftId, teams, t, lang]);

  const selectedVehicleShifts = React.useMemo(() => {
    if (selectedId == null) return [];
    return sidebarShifts.filter((shift) => isSameId(shiftOwnerId(shift, sidebarMode), selectedId));
  }, [sidebarShifts, sidebarMode, selectedId]);

  const selectedShift = React.useMemo(() => {
    return (
      selectedVehicleShifts.find((shift) => isSameId(shift.id, selectedShiftId)) ??
      selectedVehicleShifts[0]
    );
  }, [selectedShiftId, selectedVehicleShifts]);

  const selectedVehicle = selectedShift?.truck ?? selectedVehicleShifts[0]?.truck;

  React.useEffect(() => {
    if (hasInitializedShiftSelectionRef.current) return;
    if (sidebarShifts.length === 0) return;

    // Prefer shift from URL snapshot if it matches a loaded shift; otherwise
    // fall back to the first available shift. We never re-read URL after
    // mount, so this can't be clobbered by later URL writes.
    const rawRequested = initialUrlParamsRef.current.mswShiftId;
    let requested: bigint | null = null;
    if (rawRequested) {
      try {
        requested = BigInt(rawRequested);
      } catch {
        requested = null;
      }
    }
    const initialShift =
      (requested && sidebarShifts.find((shift) => isSameId(shift.id, requested))) ||
      findPreferredShift(sidebarShifts);

    setSelectedId(shiftOwnerId(initialShift, sidebarMode)!);
    applySelectedShift(initialShift.id);

    // Re-assert initial shift in URL on the next frame to avoid losing
    // `mswShiftId` when another hook patches search params during mount.
    const rafId = requestAnimationFrame(() => {
      updateUrlParams({ mswShiftId: String(initialShift.id) });
    });

    hasInitializedShiftSelectionRef.current = true;

    return () => cancelAnimationFrame(rafId);
  }, [sidebarShifts, sidebarMode, applySelectedShift, updateUrlParams]);

  // After the date, company or category changes, the selected truck or driver may be
  // gone from the new list; pick again the way the first load does instead of leaving
  // the view empty. A null selection means the initial pick above has not rendered
  // yet; replacing it here would drop the shift requested in the URL.
  React.useEffect(() => {
    if (!hasInitializedShiftSelectionRef.current || sidebarShifts.length === 0) return;
    if (selectedId == null) return;
    if (selectedVehicleShifts.length > 0) return;

    const preferred = findPreferredShift(sidebarShifts);
    setSelectedId(shiftOwnerId(preferred, sidebarMode)!);
    applySelectedShift(preferred.id);
  }, [sidebarShifts, sidebarMode, selectedId, selectedVehicleShifts, applySelectedShift]);

  React.useEffect(() => {
    if (selectedId == null || selectedVehicleShifts.length === 0) {
      if (selectedShiftId !== null) {
        applySelectedShift(null);
      }
      return;
    }

    const hasSelectedShift =
      selectedShiftId != null &&
      selectedVehicleShifts.some((shift) => isSameId(shift.id, selectedShiftId));
    if (hasSelectedShift) return;

    const preferred =
      selectedVehicleShifts.find(
        (shift) =>
          !isShiftTerminal(shift.status) &&
          (routesByShiftId.get(shift.id) ?? []).some(
            (route) => route.status === ConnectProto.RouteStatus.NOT_APPROVED,
          ),
      ) ?? findPreferredShift(selectedVehicleShifts);
    applySelectedShift(preferred.id);
  }, [selectedShiftId, selectedId, selectedVehicleShifts, routesByShiftId, applySelectedShift]);

  // Switching lists keeps the open shift: select its truck or driver in the other list.
  const setSidebarMode = React.useCallback(
    (mode: SidebarMode) => {
      setSidebarModeState(mode);
      updateUrlParams({ sidebar: mode === "drivers" ? mode : null });
      setSelectedId((selectedShift && shiftOwnerId(selectedShift, mode)) ?? null);
    },
    [selectedShift, updateUrlParams],
  );

  const selectedShiftRoutes = React.useMemo(() => {
    if (!selectedShiftId) return EMPTY_ROUTES;
    return routesByShiftId.get(selectedShiftId) ?? EMPTY_ROUTES;
  }, [routesByShiftId, selectedShiftId]);

  const hasUndefinedRouteStatus = React.useMemo(() => {
    return selectedShiftRoutes.some(
      (route) => route.status === ConnectProto.RouteStatus.NOT_APPROVED,
    );
  }, [selectedShiftRoutes]);

  const selectedShiftRoutesApproval = React.useMemo<"approved" | "not-approved" | null>(() => {
    if (selectedShiftRoutes.length === 0) return null;
    return hasUndefinedRouteStatus ? "not-approved" : "approved";
  }, [hasUndefinedRouteStatus, selectedShiftRoutes.length]);

  // Track shift changes and reset user-closed flag when switching shifts
  React.useEffect(() => {
    if (!selectedShiftId) return;

    if (!isSameId(previousShiftIdRef.current, selectedShiftId)) {
      const isInitialSelection = previousShiftIdRef.current === null;
      shiftsWithUserClosedRouteRef.current.delete(selectedShiftId);
      previousShiftIdRef.current = selectedShiftId;
      // When shift changes, clear expanded route for this shift so it can auto-expand
      setExpandedRouteByShiftId((prevMap) => {
        const nextMap = new Map(prevMap);
        nextMap.delete(selectedShiftId);
        return nextMap;
      });
      // Don't clobber URL routeId on first selection — the auto-expand effect
      // will restore it from the URL snapshot. Only clear on real shift switches.
      if (!isInitialSelection) {
        updateUrlParams({
          mswShiftId: String(selectedShiftId),
          mswRouteId: null,
        });
      }
    }
  }, [selectedShiftId, updateUrlParams]);

  React.useEffect(() => {
    if (!selectedShiftId) return;

    if (selectedShiftRoutes.length === 0) {
      if (allRoutesLoading) {
        return;
      }
      if (expandedRouteByShiftId.has(selectedShiftId)) {
        applyExpandedRoute(selectedShiftId, null);
      }
      return;
    }

    const currentExpRouteId = expandedRouteByShiftId.get(selectedShiftId) ?? null;
    const hasCurrentRoute = selectedShiftRoutes.some((route) =>
      isSameId(route.id, currentExpRouteId),
    );
    if (hasCurrentRoute) return;

    // First render after data load: try to restore route from URL snapshot.
    if (!hasAppliedInitialRouteFromUrlRef.current) {
      const rawRouteId = initialUrlParamsRef.current.mswRouteId;
      if (rawRouteId) {
        try {
          const requestedId = BigInt(rawRouteId);
          const requestedRoute = selectedShiftRoutes.find((route) =>
            isSameId(route.id, requestedId),
          );
          if (requestedRoute) {
            hasAppliedInitialRouteFromUrlRef.current = true;
            applyExpandedRoute(selectedShiftId, requestedId);
            return;
          }
        } catch {
          // ignore malformed URL param
        }
      }
      hasAppliedInitialRouteFromUrlRef.current = true;
    }

    // Auto-select first route if none is selected and user didn't explicitly close it
    if (
      !currentExpRouteId &&
      selectedShiftRoutes.length > 0 &&
      !shiftsWithUserClosedRouteRef.current.has(selectedShiftId)
    ) {
      const sortedRoutes = [...selectedShiftRoutes].sort((a, b) => {
        if (a.id < b.id) return -1;
        if (a.id > b.id) return 1;
        return 0;
      });
      applyExpandedRoute(selectedShiftId, sortedRoutes[0].id);
    }
  }, [
    selectedShiftId,
    selectedShiftRoutes,
    allRoutesLoading,
    expandedRouteByShiftId,
    applyExpandedRoute,
  ]);

  const handleSidebarSelect = React.useCallback(
    (id: bigint, preferredShiftId?: bigint) => {
      setSelectedId(id);
      const vehicleShifts = sidebarShifts.filter((shift) =>
        isSameId(shiftOwnerId(shift, sidebarMode), id),
      );
      const requestedShift =
        preferredShiftId != null &&
        vehicleShifts.find((shift) => isSameId(shift.id, preferredShiftId));

      const targetShift = requestedShift || findPreferredShift(vehicleShifts);
      applySelectedShift(targetShift?.id ?? null);
    },
    [sidebarShifts, sidebarMode, applySelectedShift],
  );

  const handleToggleShift = React.useCallback(
    (shiftId: bigint) => {
      applySelectedShift(shiftId);
    },
    [applySelectedShift],
  );

  const handleExpandRoute = React.useCallback(
    (shiftId: bigint, routeId: bigint | null) => {
      applySelectedShift(shiftId);
      const currentRouteId = expandedRouteByShiftId.get(shiftId) ?? null;
      let nextRouteId: bigint | null;
      if (routeId === null) {
        nextRouteId = null;
        shiftsWithUserClosedRouteRef.current.add(shiftId);
      } else if (currentRouteId !== null && isSameId(currentRouteId, routeId)) {
        // Toggling the same route off
        nextRouteId = null;
        shiftsWithUserClosedRouteRef.current.add(shiftId);
      } else {
        nextRouteId = routeId;
        shiftsWithUserClosedRouteRef.current.delete(shiftId);
      }
      applyExpandedRoute(shiftId, nextRouteId);
    },
    [applySelectedShift, applyExpandedRoute, expandedRouteByShiftId],
  );

  const handleRoutesUpdate = React.useCallback((shiftId: bigint, routes: ConnectProto.Route[]) => {
    setRoutesByShiftId((prevMap) => {
      const nextMap = new Map(prevMap);
      nextMap.set(shiftId, routes);
      return nextMap;
    });
  }, []);

  const handleLoadingUpdate = React.useCallback((shiftId: bigint, isLoading: boolean) => {
    setLoadingByShiftId((prevMap) => {
      const nextMap = new Map(prevMap);
      nextMap.set(shiftId, isLoading);
      return nextMap;
    });
  }, []);

  const handleSelectionMapUpdate = React.useCallback(
    (shiftId: bigint, selectionMap: Map<bigint, Set<bigint>>) => {
      setSelectionByShiftId((prevMap) => {
        const nextMap = new Map(prevMap);
        nextMap.set(shiftId, selectionMap);
        return nextMap;
      });
    },
    [],
  );

  const visibleRoutes = React.useMemo(() => {
    return selectedVehicleShifts.flatMap((shift) => routesByShiftId.get(shift.id) ?? EMPTY_ROUTES);
  }, [routesByShiftId, selectedVehicleShifts]);

  const mergedSelectionMap = React.useMemo(() => {
    const mergedMap = new Map<bigint, Set<bigint>>();

    for (const shift of selectedVehicleShifts) {
      const routeSelectionMap = selectionByShiftId.get(shift.id);
      if (!routeSelectionMap) continue;

      for (const [routeId, selectedIds] of routeSelectionMap.entries()) {
        mergedMap.set(routeId, new Set(selectedIds));
      }
    }

    return mergedMap;
  }, [selectionByShiftId, selectedVehicleShifts]);

  React.useEffect(() => {
    onTransferContextChange?.({
      selectedDate,
      routes: visibleRoutes,
      selectionMap: mergedSelectionMap,
    });
  }, [mergedSelectionMap, onTransferContextChange, selectedDate, visibleRoutes]);

  const activeRouteForMap = React.useMemo(() => {
    if (!focusedExpandedRouteId) return null;
    return selectedShiftRoutes.find((route) => isSameId(route.id, focusedExpandedRouteId)) ?? null;
  }, [focusedExpandedRouteId, selectedShiftRoutes]);

  // Numbered like the table's "#" column, so it has to agree with the table and the
  // route line rather than with the API's order.
  const mapObjects = React.useMemo<MapObject[]>(
    () => buildStepPins(numberRouteJobs(activeRouteForMap ? [activeRouteForMap] : [], "step")),
    [activeRouteForMap],
  );

  // The job picked in the table or on the map; `seq` makes picking it again fly back to it.
  // A pick from the table moves the map, a pick from the map scrolls the table.
  const [focusedJob, setFocusedJob] = React.useState<{
    id: bigint;
    seq: number;
    from: "table" | "map";
  } | null>(null);
  // Picking the focused job again unpicks it and shows the whole route.
  const [routeFitSeq, setRouteFitSeq] = React.useState(0);
  const focusedJobRef = React.useRef(focusedJob);
  focusedJobRef.current = focusedJob;
  const focusJob = React.useCallback((id: bigint) => {
    if (focusedJobRef.current?.id === id) {
      setFocusedJob(null);
      setRouteFitSeq((n) => n + 1);
      return;
    }
    setFocusedJob((prev) => ({ id, seq: (prev?.seq ?? 0) + 1, from: "table" }));
  }, []);
  const handleMapPinClick = React.useCallback((pin: MapObject) => {
    if (pin.jobId == null) return;
    const id = pin.jobId;
    setFocusedJob((prev) => ({ id, seq: (prev?.seq ?? 0) + 1, from: "map" }));
  }, []);
  React.useEffect(() => setFocusedJob(null), [activeRouteForMap?.id]);

  const mapFocusPoint = React.useMemo<FocusPoint | null>(() => {
    if (focusedJob?.from !== "table") return null;
    const pin = mapObjects.find((o) => o.jobId === focusedJob.id);
    return pin ? { lat: pin.lat, lon: pin.lon, key: `${focusedJob.id}:${focusedJob.seq}` } : null;
  }, [focusedJob, mapObjects]);

  const mapTrucks = React.useMemo<MapObject[]>(() => {
    const truckId = selectedShift?.truckId;
    if (!truckId) return [];

    return [
      {
        id: Number(truckId),
        name: selectedVehicle?.plateNumber ?? String(truckId),
        type: "truck" as InfoType,
        lat: activeRouteForMap?.sourceLat ?? 43.238949,
        lon: activeRouteForMap?.sourceLon ?? 76.889709,
        angle: 0,
        truckType: ConnectProto.TruckTypeSchema.values.find(
          (v) => v.number === selectedVehicle?.type,
        )?.name,
        ownerOrganizationId: selectedVehicle?.ownerOrganizationId,
      },
    ];
  }, [
    activeRouteForMap,
    selectedShift?.truckId,
    selectedVehicle?.plateNumber,
    selectedVehicle?.type,
    selectedVehicle?.ownerOrganizationId,
  ]);

  // A route carries only its destination's id, and the end point is often a base,
  // not a landfill. Another organization's location is not readable: 404, generic label.
  // BigInt(): int64 ids reach here as JSON strings despite their type.
  const destinationId = activeRouteForMap?.destinationLocationId
    ? BigInt(activeRouteForMap.destinationLocationId)
    : undefined;
  const { data: destination } = useGetLocationByIdQuery(
    { id: destinationId ?? 0n, ownerOrganizationId: BigInt(activeCompanyId ?? 0) },
    { skip: !destinationId || !activeCompanyId },
  );
  const destinationName =
    destination && String(destination.id) === String(destinationId) ? destination.name : undefined;

  const mapDepots = React.useMemo<MapObject[]>(() => {
    if (activeRouteForMap?.destinationLat == null || activeRouteForMap?.destinationLon == null) {
      return [];
    }

    return [
      {
        id: `route-${activeRouteForMap.id}-destination`,
        name: destinationName ?? t(Namespace.Routes, "finalDestination"),
        locationType: destinationName ? destination?.type : undefined,
        type: "depot" as InfoType,
        lat: activeRouteForMap.destinationLat,
        lon: activeRouteForMap.destinationLon,
      },
    ];
  }, [activeRouteForMap, destinationName, destination?.type, t]);

  const focusedShiftLoading = allRoutesLoading;

  return {
    selectedDate,
    setSelectedDate,
    vehicleSearch,
    setVehicleSearch,
    sidebarMode,
    setSidebarMode,
    sidebarSections,
    selectedId,
    selectedVehicle,
    selectedVehicleShifts,
    selectedShift,
    selectedShiftRoutesApproval,
    expandedRouteByShiftId,
    focusedShiftLoading,
    activeRouteForMap,
    mapObjects,
    focusedJobId: focusedJob?.id ?? null,
    scrollToFocusedJob: focusedJob?.from === "map",
    focusJob,
    routeFitSeq,
    handleMapPinClick,
    mapFocusPoint,
    mapTrucks,
    mapDepots,
    routesByShiftId,
    allRoutesByShiftId,
    handleSidebarSelect,
    handleToggleShift,
    handleExpandRoute,
    handleRoutesUpdate,
    handleLoadingUpdate,
    handleSelectionMapUpdate,
    refetchAllRoutes,
    refetchShifts,
  };
};
